import {
  EDefaultNativeSize,
  EDefaultPushSize,
} from './../../types/publisher/inventory';
import { typeTraffic } from '@/types';
import {
  TPlacementWidgetParams,
  IWidgetParams,
  IPushWidgetParams,
  ETrigger,
  IBannerWidgetParams,
  INativeWidgetParams,
} from '@/types/publisher/inventory';
import _ from 'lodash';

const DEFAULT_AD_FOR_ASSETS: any = {
  title: { required: true, len: 80 },
  image: { required: true, sizes: [] },
  desc: { required: true, len: 80 },
  sponsoredBy: { required: false },
};

const ASSETS = {
  title: { id: 1, key: 'title', type: 0 },
  image: { id: 2, key: 'image', type: 0 },
  icon: { id: 3, key: 'icon', type: 0 },
  sponsoredBy: { id: 4, key: 'sponsoredBy', type: 1 }, // please note that type of SPONSORED is also 1
  desc: { id: 5, key: 'desc', type: 2 }, // please note that type of BODY is also set to 2
  price: { id: 6, key: 'price', type: 6 },
  salePrice: { id: 7, key: 'saleprice', type: 7 },
  displayUrl: { id: 8, key: 'displayurl', type: 11 },
  cta: { id: 9, key: 'cta', type: 12 },
};

const IMAGE_ASSETS_TYPE: { image: number; icon: number } = {
  icon: 1,
  image: 3,
};

const DEFAULT_TITLE_LENGTH = 80;
const DEFAULT_NATIVE_IMAGE_WIDTH = EDefaultNativeSize.width;
const DEFAULT_NATIVE_IMAGE_HEIGHT = EDefaultNativeSize.height;
const DEFAULT_PUSH_IMAGE_WIDTH = EDefaultPushSize.width;
const DEFAULT_PUSH_IMAGE_HEIGHT = EDefaultPushSize.height;
const DEFAULT_ICON_WIDTH = 50;
const DEFAULT_ICON_HEIGHT = 50;

export function createWidgetParams(
  widgetParams: TPlacementWidgetParams,
  widgetParamsInit: IWidgetParams
): IWidgetParams {
  const {
    openTrigger,
    openTimeout,
    openAnchor,
    openScroll,
    closeTrigger,
    closeScroll,
    closeTimeout,
    verticalPosition: vPosition,
    horizontalPosition: hPosition,
  } = widgetParams as IPushWidgetParams;
  const newWidgetParams = {} as IWidgetParams;

  _.forEach(widgetParamsInit, (value: any, key: string) => {
    if (key === 'openTriggerType') {
      newWidgetParams[key] = openTrigger ?? value;
      return;
    } else if (key === 'openTriggerValue') {
      switch (openTrigger) {
        case ETrigger.TIMEOUT:
          newWidgetParams[key] = openTimeout ?? value;
          break;
        case ETrigger.ANCHOR_ID:
          newWidgetParams[key] = openAnchor ?? value;
          break;
        case ETrigger.ANCHOR_CLASS:
          newWidgetParams[key] = openAnchor ?? value;
          break;
        case ETrigger.SCROLL:
          newWidgetParams[key] = openScroll ?? value;
          break;
        default:
          newWidgetParams[key] = value;
      }
      return;
    } else if (key === 'closeTriggerType') {
      newWidgetParams[key] = closeTrigger ?? value;
      return;
    } else if (key === 'closeTriggerValue') {
      switch (closeTrigger) {
        case ETrigger.TIMEOUT:
          newWidgetParams[key] = closeTimeout ?? value;
          break;
        case ETrigger.SCROLL:
          newWidgetParams[key] = closeScroll ?? value;
          break;
        default:
          newWidgetParams[key] = value;
      }
      return;
    } else if (key === 'position') {
      newWidgetParams[key] =
        vPosition && hPosition ? `${vPosition}_${hPosition}` : value;
      return;
    }

    newWidgetParams[key] = widgetParams[key] ?? value;
  });

  return newWidgetParams;
}

export function createBannerWidgetParams(
  widgetParams: IWidgetParams
): IBannerWidgetParams {
  const bannerWidgetParams = {} as IBannerWidgetParams;
  bannerWidgetParams.placement_type = widgetParams.type;
  bannerWidgetParams.element = widgetParams.element;
  bannerWidgetParams.width = Number(widgetParams.width);
  bannerWidgetParams.height = Number(widgetParams.height);
  return bannerWidgetParams;
}

export function createNativeWidgetParams(widgetParams): INativeWidgetParams {
  const nativeWidgetParams = {} as INativeWidgetParams;
  nativeWidgetParams.placement_type = widgetParams.type;
  nativeWidgetParams.element = widgetParams.element;
  nativeWidgetParams.width = Number(widgetParams.width);
  nativeWidgetParams.height = Number(widgetParams.height);
  nativeWidgetParams.cols = Number(widgetParams.cols);
  nativeWidgetParams.rows = Number(widgetParams.rows);
  if (widgetParams.useMaxHeight) {
    nativeWidgetParams.useMaxHeight = widgetParams.useMaxHeight;
    nativeWidgetParams.maxHeight = Number(widgetParams.maxHeight);
  }
  nativeWidgetParams.bids = createNativeBids(
    widgetParams.cols,
    widgetParams.rows
  );

  return nativeWidgetParams;
}

export function createPushWidgetParams(widgetParams): IPushWidgetParams {
  const pushWidgetParams = {} as IPushWidgetParams;
  const position = widgetParams.position.split('_');

  pushWidgetParams.placement_type = widgetParams.type;
  pushWidgetParams.typeDevice = widgetParams.typeDevice;
  pushWidgetParams.verticalPosition = position[0];
  pushWidgetParams.horizontalPosition = position[1];
  pushWidgetParams.width = Number(widgetParams.width);
  pushWidgetParams.height = Number(widgetParams.height);
  pushWidgetParams.mobileVerticalMargin = Number(
    widgetParams.mobileVerticalMargin
  );
  pushWidgetParams.desktopVerticalMargin = Number(
    widgetParams.desktopVerticalMargin
  );
  pushWidgetParams.desktopHorizontalMargin = Number(
    widgetParams.desktopHorizontalMargin
  );
  pushWidgetParams.openTrigger = widgetParams.openTriggerType;
  switch (widgetParams.openTriggerType) {
    case ETrigger.TIMEOUT:
      pushWidgetParams.openTimeout = Number(widgetParams.openTriggerValue);
      break;
    case ETrigger.ANCHOR_ID:
      pushWidgetParams.openAnchor = widgetParams.openTriggerValue;
      break;
    case ETrigger.ANCHOR_CLASS:
      pushWidgetParams.openAnchor = widgetParams.openTriggerValue;
      break;
    case ETrigger.SCROLL:
      pushWidgetParams.openScroll = Number(widgetParams.openTriggerValue);
      break;
  }
  if (widgetParams.useCloseTrigger) {
    pushWidgetParams.useCloseTrigger = widgetParams.useCloseTrigger;
    pushWidgetParams.closeTrigger = widgetParams.closeTriggerType;
    switch (widgetParams.closeTriggerType) {
      case ETrigger.TIMEOUT:
        pushWidgetParams.closeTimeout = Number(widgetParams.closeTriggerValue);
        break;
      case ETrigger.SCROLL:
        pushWidgetParams.closeScroll = Number(widgetParams.closeTriggerValue);
        break;
    }
  }
  pushWidgetParams.bids = createPushBids();
  return pushWidgetParams;
}

function createNativeBids(cols: number, rows: number): Array<any> {
  let countAds: number = cols * rows;
  const nativeMediaParams = [] as Array<any>;

  while (countAds) {
    const defaultAdForAssets = _.cloneDeep(DEFAULT_AD_FOR_ASSETS);
    nativeMediaParams.push(defaultAdForAssets);
    countAds--;
  }
  const bids = createAssets(nativeMediaParams, typeTraffic.NATIVE);
  return bids;
}

function createPushBids(): Array<any> {
  const defaultAdForAssets = _.cloneDeep(DEFAULT_AD_FOR_ASSETS);
  const pushMediaParams = [] as Array<any>;

  pushMediaParams.push(defaultAdForAssets);
  const bids = createAssets(pushMediaParams, typeTraffic.PUSH);
  return bids;
}

function createAssets(units, type) {
  if (!units?.length) return;

  const bids: any = [];
  let minImageWidth: any = DEFAULT_NATIVE_IMAGE_WIDTH;
  let minImageHeight: any = DEFAULT_NATIVE_IMAGE_HEIGHT;

  if (type === 'push') {
    minImageWidth = DEFAULT_PUSH_IMAGE_WIDTH;
    minImageHeight = DEFAULT_PUSH_IMAGE_HEIGHT;
  }

  units.forEach((asset) => {
    const bid: any = {
      [type]: {
        request: {
          assets: [],
        },
      },
    };
    for (const key in asset) {
      let assetObj = {};

      switch (key) {
        // Title
        case ASSETS.title.key:
          assetObj = {
            id: ASSETS.title.id,
            required: asset[key].required ? 1 : 0,
            title: {
              len: asset[key].len || DEFAULT_TITLE_LENGTH,
            },
          };
          break;

        // Image
        case ASSETS.image.key:
          // eslint-disable-next-line no-case-declarations
          const img = {
            type: IMAGE_ASSETS_TYPE.image,
          };

          // eslint-disable-next-line no-case-declarations
          const wmin =
            asset[key].minsizes && asset[key].minsizes.length
              ? asset[key].minsizes[0]
              : minImageWidth;
          // eslint-disable-next-line no-case-declarations
          const hmin =
            asset[key].minsizes && asset[key].minsizes.length
              ? asset[key].minsizes[1]
              : minImageHeight;
          // eslint-disable-next-line no-case-declarations
          const w =
            asset[key].sizes && asset[key].sizes.length
              ? asset[key].sizes[0]
              : 0;
          // eslint-disable-next-line no-case-declarations
          const h =
            asset[key].sizes && asset[key].sizes.length
              ? asset[key].sizes[1]
              : 0;

          if (wmin) {
            img['wmin'] = wmin;
          }
          if (hmin) {
            img['hmin'] = hmin;
          }
          if (w) {
            img['w'] = w;
          }
          if (h) {
            img['h'] = h;
          }

          assetObj = {
            id: ASSETS.image.id,
            required: asset[key].required ? 1 : 0,
            img,
          };
          break;

        // Icon
        case ASSETS.icon.key:
          assetObj = {
            id: ASSETS.icon.id,
            required: asset[key].required ? 1 : 0,
            img: {
              type: IMAGE_ASSETS_TYPE.icon,
              w:
                asset[key].sizes && asset[key].sizes.length
                  ? asset[key].sizes[0]
                  : DEFAULT_ICON_WIDTH,
              h:
                asset[key].sizes && asset[key].sizes.length
                  ? asset[key].sizes[1]
                  : DEFAULT_ICON_HEIGHT,
            },
          };
          break;
        case ASSETS.sponsoredBy.key:
        case ASSETS.desc.key:
        case ASSETS.price.key:
        case ASSETS.salePrice.key:
        case ASSETS.displayUrl.key:
        case ASSETS.cta.key:
          assetObj = dataAsset(key, asset);
          break;
      }

      bid[type].request.assets.push(assetObj);
    }
    bids.push(bid);
  });

  function dataAsset(key, asset) {
    const assetObj = {
      id: ASSETS[key].id,
      required: asset[key].required ? 1 : 0,
      data: {
        type: ASSETS[key].type,
      },
    };

    const len = asset[key].len;
    if (len) {
      assetObj.data['len'] = len;
    }

    return assetObj;
  }

  return bids;
}
